import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { ArrowRight, Shield, Zap, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || (isRegister && !name)) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      if (isRegister) {
        const result = await register(name, email, password);
        if (result.pending_approval) {
          toast.success('Account created! Please wait for admin approval before signing in.', { duration: 8000 });
          setIsRegister(false);
          setName('');
          setEmail('');
          setPassword('');
        } else {
          navigate('/dashboard');
        }
      } else {
        await login(email, password);
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Authentication failed';
      if (err.response?.status === 403) {
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#FFFBF2]">
        <div className="flex flex-col justify-center px-16 py-12 w-full relative z-10">
          <div className="flex items-center gap-3 mb-14">
            <img src="/jiofinance-logo.svg" alt="JioFinance" className="w-11 h-11" />
            <div>
              <span className="font-heading font-bold text-xl tracking-tight">JioFinance</span>
              <span className="font-heading font-medium text-lg text-muted-foreground ml-1.5">Avatar Studio</span>
            </div>
          </div>
          
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-4" style={{ lineHeight: '1.1' }}>
            Turn ideas into<br />avatar videos
          </h1>
          <p className="text-lg text-muted-foreground mb-14 max-w-md leading-relaxed">
            From concept to AI-powered spokesperson video in minutes. Just add your idea, pick an avatar, and go.
          </p>

          <div className="space-y-5">
            {[
              { icon: Zap, text: 'AI script generation from your idea' },
              { icon: Shield, text: 'Premium avatars & natural voices' },
              { icon: Download, text: 'Download production-ready MP4' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-[hsl(38,78%,56%)]/10 flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-[hsl(38,78%,45%)]" />
                </div>
                <span className="text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Decorative amber circle */}
        <div className="absolute -bottom-32 -right-32 w-80 h-80 rounded-full bg-[hsl(38,78%,56%)]/5" />
        <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full bg-[hsl(38,78%,56%)]/4" />
      </div>

      {/* Right login panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <img src="/jiofinance-logo.svg" alt="JioFinance" className="w-10 h-10" />
            <span className="font-heading font-bold text-xl tracking-tight">JioFinance Avatar Studio</span>
          </div>

          <Card className="rounded-2xl shadow-[0_1px_0_rgba(15,23,42,0.04),0_18px_40px_rgba(15,23,42,0.08)] border">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-2xl font-bold">
                {isRegister ? 'Create account' : 'Welcome back'}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {isRegister ? 'Start creating avatar videos today' : 'Sign in to your workspace'}
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {isRegister && (
                  <div className="space-y-1.5">
                    <Label htmlFor="name">Full name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="rounded-xl"
                      data-testid="register-name-input"
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="rounded-xl"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="rounded-xl"
                    data-testid="login-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-xl gap-2 h-11"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      {isRegister ? 'Create account' : 'Sign in'}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>

              <Separator className="my-4" />

              <p className="text-center text-sm text-muted-foreground">
                {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button
                  type="button"
                  onClick={() => setIsRegister(!isRegister)}
                  className="text-[hsl(38,78%,45%)] font-semibold hover:underline"
                  data-testid="toggle-auth-mode"
                >
                  {isRegister ? 'Sign in' : 'Create one'}
                </button>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
